import { useEffect, forwardRef, useImperativeHandle, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Chip,
  Button,
  IconButton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { usePlanoVelesService } from '../../../use-cases/planos-veles';
import { IPlanoCompletoMultiRange, PlanosVelesTabProps, PlanosVelesTabRef, PlanoViewMode, TipoPlano } from '../../../application/models/IPlano';
import { useAppDispatch, useAppSelector } from '../../../presentation/hooks/store-hooks';
import {
  setExpiracaoPOC,
  setExpiracaoPre,
  setQuantidadeMaximaFee,
  setQuantidadePOC,
  setQuantidadePre,
  setRangesFeeExcedente,
  setRangesPosPago,
  setTipoPlano,
  setValorFee,
  setValorPre,
  setValorUnitarioPre
} from '../../../application/states/planoVeles/planoVeles-slice';

import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import {
  plano as formatarPlano,
  formatValorUnitario,
  formatQuantidadeComRaw,
  formatQuantidadeRangeComRaw,
  formatValorMensalComRaw,
  formatValorUnitarioPre,
} from '../../../utils/formatters/plano/planoFormatter';

const tipoPlanoLabel: Record<TipoPlano, string> = {
  [TipoPlano.PrePago]: 'Pré Pago',
  [TipoPlano.PosPago]: 'Pós Pago',
  [TipoPlano.FeeMensal]: 'Fee Mensal',
  [TipoPlano.POC]: 'POC'
};

export const PlanosVelesTab = forwardRef<PlanosVelesTabRef, PlanosVelesTabProps>(({ IdCliente, isEditMode = false, viewMode }, ref) => {

  const dispatch = useAppDispatch();
  const editClienteForm = useAppSelector((state) => state.clientes.editClienteForm);

  const effectiveMode: PlanoViewMode = viewMode || (isEditMode ? 'edit' : 'create');
  const isReadOnly = effectiveMode === 'view';
  const shouldLoadData = effectiveMode === 'edit' || effectiveMode === 'view';

  const {
    loading,
    addRangePosPago,
    removeRangePosPago,
    updateRangePosPago,
    addRangeFeeExcedente,
    removeRangeFeeExcedente,
    updateRangeFeeExcedente,
    quantidadeMaximaFee,
    validarFormatarDadosPlano,
    plano,
    initializePlanoData,
    handlePlanoCalculation,
    tipoPlano,
    camposComErro,
    quantidadeMinimaFee,
    valorFee,
    quantidadePre,
    valorPre,
    valorUnitarioPre,
    expiracaoPre,
    quantidadePOC,
    expiracaoPOC,
    rangesPosPago,
    rangesFeeExcedente,
    dadosIniciaisCarregados,
    resetPlanoFormOnClienteChange,
    restaurarPlanoCarregado,
  } = usePlanoVelesService();

  const [valorMensalRaw, setValorMensalRaw] = useState<{ isRaw: boolean; value: string }>({ isRaw: false, value: '' });
  const [valorTotalPreRaw, setValorTotalPreRaw] = useState<{ isRaw: boolean; value: string }>({ isRaw: false, value: '' });

  const [quantidadeMaximaFeeRaw, setQuantidadeMaximaFeeRaw] = useState<{ isRaw: boolean; value: string }>({ isRaw: false, value: '' });
  const [quantidadePreRaw, setQuantidadePreRaw] = useState<{ isRaw: boolean; value: string }>({ isRaw: false, value: '' });
  const [quantidadePOCRaw, setQuantidadePOCRaw] = useState<{ isRaw: boolean; value: string }>({ isRaw: false, value: '' });

  const [rangeExcedenteMaxRaw, setRangeExcedenteMaxRaw] = useState<Record<number, string>>({});
  const [rangePosPagoMaxRaw, setRangePosPagoMaxRaw] = useState<Record<number, string>>({});
  const setExcedenteMaxRaw = (index: number, value: string) => {
    setRangeExcedenteMaxRaw((prev) => ({ ...prev, [index]: value }));
  };
  const clearExcedenteMaxRaw = (index: number) => {
    setRangeExcedenteMaxRaw((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };
  const setPosPagoMaxRaw = (index: number, value: string) => {
    setRangePosPagoMaxRaw((prev) => ({ ...prev, [index]: value }));
  };
  const clearPosPagoMaxRaw = (index: number) => {
    setRangePosPagoMaxRaw((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const [valorUnitarioPending, setValorUnitarioPending] = useState<{ fee: Record<number, string>; pos: Record<number, string> }>({ fee: {}, pos: {} });
  const setFeePending = (index: number, str: string) => {
    setValorUnitarioPending((prev) => {
      const next = { ...prev, fee: { ...prev.fee } };
      next.fee[index] = str;
      return next;
    });
  };
  const clearFeePending = (index: number) => {
    setValorUnitarioPending((prev) => {
      const next = { ...prev, fee: { ...prev.fee } };
      delete next.fee[index];
      return next;
    });
  };
  const setPosPending = (index: number, str: string) => {
    setValorUnitarioPending((prev) => {
      const next = { ...prev, pos: { ...prev.pos } };
      next.pos[index] = str;
      return next;
    });
  };
  const clearPosPending = (index: number) => {
    setValorUnitarioPending((prev) => {
      const next = { ...prev, pos: { ...prev.pos } };
      delete next.pos[index];
      return next;
    });
  };

  useEffect(() => {
    if (shouldLoadData && IdCliente) {
      resetPlanoFormOnClienteChange();
    }
  }, [IdCliente, shouldLoadData, resetPlanoFormOnClienteChange]);

  useImperativeHandle(ref, () => {
    // Verificar se o cliente está ativo
    const clienteAtivo = editClienteForm?.fields?.Ativo ?? true; // Default true para criação de novos clientes
    
    return {
      getPlanoData: () => validarFormatarDadosPlano(IdCliente),
      hasErrors: () => {
        // Se o cliente estiver inativo, não precisa validar
        if (!clienteAtivo) return false;
        return camposComErro.size > 0;
      },
      getErrorMessage: () => {
        // Se o cliente estiver inativo, não precisa validar
        if (!clienteAtivo) return '';
        
        if (camposComErro.size === 0) return '';
        if (camposComErro.has('rangeInvalido')) {
          return 'Apenas o último range pode ter valor de 999.999.999';
        }
        if (quantidadeMaximaFee > 0 && quantidadeMinimaFee > quantidadeMaximaFee) {
          return 'Quantidade Mínima não pode ser maior que Quantidade Máxima';
        }
        for (let i = 0; i < rangesFeeExcedente.length; i++) {
          const range = rangesFeeExcedente[i];
          if (range.max > 0 && range.min > range.max) {
            return 'Quantidade Mínima não pode ser maior que Quantidade Máxima';
          }
        }
        for (let i = 0; i < rangesPosPago.length; i++) {
          const range = rangesPosPago[i];
          if (range.max > 0 && range.min > range.max) {
            return 'Quantidade Mínima não pode ser maior que Quantidade Máxima';
          }
        }
        return 'Preencha todos os campos obrigatórios';
      },
    };
  }, [validarFormatarDadosPlano, IdCliente, camposComErro, quantidadeMaximaFee, quantidadeMinimaFee, rangesFeeExcedente, rangesPosPago, editClienteForm?.fields?.Ativo]);

  const initializationDoneRef = useRef(false);

  useEffect(() => {
    if (!dadosIniciaisCarregados && shouldLoadData && !initializationDoneRef.current) {
      const planoDoFormulario = editClienteForm?.fields?.plano;
      const planoDoClienteCorreto = planoDoFormulario && planoDoFormulario.IdCliente === IdCliente;

      if (planoDoClienteCorreto || plano) {
        initializePlanoData(
          shouldLoadData,
          editClienteForm,
          plano || {} as IPlanoCompletoMultiRange,
          dadosIniciaisCarregados
        );
        initializationDoneRef.current = true;
      }
    }

    if (initializationDoneRef.current && !dadosIniciaisCarregados) {
      initializationDoneRef.current = false;
    }
  }, [IdCliente, shouldLoadData, dadosIniciaisCarregados, initializePlanoData, editClienteForm, plano, effectiveMode]);

  useEffect(() => {
    handlePlanoCalculation();
  }, [handlePlanoCalculation, quantidadePre, valorPre, tipoPlano, quantidadeMaximaFee]);

  const handleTipoPlanoChange = (event: any) => {
    const novoTipo = event.target.value;

    if (shouldLoadData && restaurarPlanoCarregado(novoTipo)) {
      return;
    }

    dispatch(setTipoPlano(novoTipo));

    if (novoTipo === TipoPlano.FeeMensal) {
      dispatch(setQuantidadeMaximaFee(0));
      dispatch(setValorFee(0));
      dispatch(setRangesFeeExcedente([{ min: 1, max: 999999999, valor: 0 }]));

    } else if (novoTipo === TipoPlano.PosPago) {
      dispatch(setRangesPosPago([{ min: 1, max: 999999999, valor: 0 }]));

    } else if (novoTipo === TipoPlano.PrePago) {
      dispatch(setQuantidadePre(0));
      dispatch(setValorPre(0));
      dispatch(setValorUnitarioPre(0));
      dispatch(setExpiracaoPre(null));

    } else if (novoTipo === TipoPlano.POC) {
      dispatch(setQuantidadePOC(0));
      dispatch(setExpiracaoPOC(null));
    }
  };

  const renderFeeMensalFields = () => (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <TextField
          fullWidth
          label="Quantidade Mínima"
          type="number"
          inputProps={{ min: 1 }}
          value={1}
          disabled
          sx={{
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
            }
          }}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          label="Quantidade Máxima"
          type="text"
          inputProps={{ min: 0, max: 999999999 }}
          value={formatQuantidadeComRaw(quantidadeMaximaFeeRaw, quantidadeMaximaFee)}
          onFocus={() => {
            setQuantidadeMaximaFeeRaw({ isRaw: true, value: quantidadeMaximaFee ? formatarPlano(Math.floor(quantidadeMaximaFee)) : '' });
          }}
          onChange={(e) => {
            const digitos = e.target.value.replace(/\D/g, '');
            const limited = digitos.length > 9 ? digitos.slice(0, 9) : digitos;
            setQuantidadeMaximaFeeRaw({ isRaw: true, value: limited ? formatarPlano(limited) : '' });
            const valorNumerico = limited === '' ? 0 : parseInt(limited, 10);
            if (valorNumerico <= 999999999) {
              dispatch(setQuantidadeMaximaFee(valorNumerico));
              setTimeout(() => handlePlanoCalculation(), 0);
            }
          }}
          onBlur={() => {
            if (quantidadeMaximaFeeRaw.isRaw) {
              const digitos = quantidadeMaximaFeeRaw.value.replace(/\D/g, '');
              const valorNumerico = digitos === '' ? 0 : Math.min(999999999, parseInt(digitos, 10) || 0);
              dispatch(setQuantidadeMaximaFee(valorNumerico));
              setTimeout(() => handlePlanoCalculation(), 0);
              setQuantidadeMaximaFeeRaw({ isRaw: false, value: '' });
            }
          }}
          disabled={isReadOnly}
          error={camposComErro.has('quantidadeMaximaFee')}
          helperText={camposComErro.has('quantidadeMaximaFee') ? 'Campo obrigatório' : ''}
          onKeyDown={(e) => {
            if (["e", "E", "-", "+", ","].includes(e.key)) {
              e.preventDefault();
            }
          }}
          onWheel={(e) => (e.target as HTMLInputElement).blur()}
          sx={{
            minHeight: '80px',
            '& .MuiFormHelperText-root': {
              marginTop: '4px'
            },
            '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
            '& input[type=number]': {
              MozAppearance: 'textfield',
            },
          }}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          label="Valor Mensal (R$)"
          type="text"
          inputProps={{ min: 1, step: 0.01, inputMode: 'decimal' }}
          value={formatValorMensalComRaw(valorMensalRaw, valorFee)}
          onFocus={() => {
            const valorFormatado = formatValorMensalComRaw({ isRaw: false, value: '' }, valorFee);
            setValorMensalRaw({ isRaw: true, value: valorFormatado === '0,00' ? '' : valorFormatado });
          }}
          onChange={(e) => {
            const digitos = e.target.value.replace(/\D/g, '').slice(0, 15);
            if (!digitos) {
              setValorMensalRaw({ isRaw: true, value: '' });
              dispatch(setValorFee(0));
              return;
            }

            const valorNumerico = Number(digitos) / 100;
            const valorFormatado = formatValorMensalComRaw({ isRaw: false, value: '' }, valorNumerico);
            setValorMensalRaw({ isRaw: true, value: valorFormatado });
            dispatch(setValorFee(valorNumerico));
          }}
          onBlur={() => {
            if (valorMensalRaw.isRaw) {
              setValorMensalRaw({ isRaw: false, value: '' });
            }
          }}
          disabled={isReadOnly}
          error={camposComErro.has('valorFee')}
          helperText={camposComErro.has('valorFee') ? 'Campo obrigatório' : ''}
          onKeyDown={(e) => {
            if (['e', 'E', '-', '+'].includes(e.key)) {
              e.preventDefault();
            }
          }}
          onWheel={(e) => (e.target as HTMLInputElement).blur()}
          sx={{
            '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
            '& input[type=number]': {
              MozAppearance: 'textfield',
            },
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>
          Ranges de Excedente
        </Typography>

        {rangesFeeExcedente.map((range: any, index: number) => (
          <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Grid container spacing={3} alignItems="flex-start">
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Quantidade Mínima"
                  type="text"
                  inputProps={{ min: 1 }}
                  value={formatarPlano(range.min)}
                  disabled
                  sx={{
                    minHeight: '80px',
                    '& .MuiFormHelperText-root': {
                      marginTop: '4px'
                    },
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Quantidade Máxima"
                  type="text"
                  inputProps={{
                    min: 0,
                    max: 999999999
                  }}
                  value={formatQuantidadeRangeComRaw(rangeExcedenteMaxRaw[index], range.max)}
                  onFocus={() => {
                    const v = range.max;
                    setExcedenteMaxRaw(index, (v !== undefined && v !== '' && Number.isFinite(Number(v)) ? formatarPlano(String(Math.floor(Number(v)))) : ''));
                  }}
                  onChange={(e) => {
                    const digitos = e.target.value.replace(/\D/g, '');
                    const limited = digitos.length > 9 ? digitos.slice(0, 9) : digitos;
                    setExcedenteMaxRaw(index, limited ? formatarPlano(limited) : '');
                    const valorNumerico = limited === '' ? 0 : parseInt(limited, 10);
                    if (valorNumerico <= 999999999) {
                      updateRangeFeeExcedente(index, 'max', valorNumerico);
                    }
                  }}
                  onBlur={() => {
                    if (index in rangeExcedenteMaxRaw) {
                      const digitos = rangeExcedenteMaxRaw[index].replace(/\D/g, '');
                      const valorNumerico = digitos === '' ? 0 : Math.min(999999999, parseInt(digitos, 10) || 0);
                      updateRangeFeeExcedente(index, 'max', valorNumerico);
                      clearExcedenteMaxRaw(index);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (["e", "E", "-", "+", ","].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                  disabled={isReadOnly || index === rangesFeeExcedente.length - 1}
                  error={camposComErro.has(`rangeExcedenteMax${index}`)}
                  helperText={
                    camposComErro.has(`rangeExcedenteMax${index}`)
                      ? 'Campo obrigatório'
                      : ''
                  }
                  sx={{
                    minHeight: '80px',
                    '& .MuiFormHelperText-root': {
                      marginTop: '4px'
                    },
                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Valor Unitário (R$)"
                  type="text"
                  inputProps={{
                    min: 0,
                    step: 0.0001,
                    inputMode: "decimal",
                    pattern: "[0-9]*[.,]?[0-9]*"
                  }}
                  value={index in valorUnitarioPending.fee ? valorUnitarioPending.fee[index] : formatValorUnitario(range.valor)}
                  onChange={(e) => {
                    const digitos = e.target.value.replace(/\D/g, '').slice(0, 15);
                    if (!digitos) {
                      setFeePending(index, '');
                      updateRangeFeeExcedente(index, 'valor', 0);
                      return;
                    }

                    const valorNumerico = Number(digitos) / 10000;
                    const valorFormatado = formatValorUnitario(valorNumerico);
                    setFeePending(index, valorFormatado);
                    updateRangeFeeExcedente(index, 'valor', valorNumerico);
                  }}
                  onBlur={() => {
                    if (index in valorUnitarioPending.fee) {
                      clearFeePending(index);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (['e', 'E', '-', '+'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                  disabled={isReadOnly}
                  error={camposComErro.has(`rangeExcedente${index}`) || camposComErro.has(`rangeExcedenteDecimais${index}`)}
                  helperText={
                    camposComErro.has(`rangeExcedenteDecimais${index}`)
                      ? 'Máximo 4 casas decimais'
                      : camposComErro.has(`rangeExcedente${index}`)
                        ? 'Campo obrigatório'
                        : ''
                  }
                  sx={{
                    minHeight: '80px',
                    '& .MuiFormHelperText-root': {
                      marginTop: '4px'
                    },
                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0
                    },
                    '& input[type=number]': {
                      MozAppearance: 'textfield'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                {rangesFeeExcedente.length > 1 && !isReadOnly && (
                  <IconButton
                    onClick={() => removeRangeFeeExcedente(index)}
                    color="error"
                    title="Remover range"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          </Box>
        ))}

        {!isReadOnly && (
          <Button
            startIcon={<AddIcon />}
            onClick={addRangeFeeExcedente}
            variant="outlined"
            size="small"
          >
            Adicionar Range
          </Button>
        )}
      </Grid>
    </Grid>
  );

  const renderPosPagoFields = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom>
          Ranges de Consumo
        </Typography>

        {rangesPosPago.map((range: any, index: number) => (
          <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Grid container spacing={3} alignItems="flex-start">
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Quantidade Mínima"
                  type="text"
                  inputProps={{ min: 1 }}
                  value={formatarPlano(range.min)}
                  disabled
                  sx={{
                    minHeight: '80px',
                    '& .MuiFormHelperText-root': {
                      marginTop: '4px'
                    },
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Quantidade Máxima"
                  type="text"
                  inputProps={{
                    min: 0,
                    max: 999999999
                  }}
                  value={formatQuantidadeRangeComRaw(rangePosPagoMaxRaw[index], range.max)}
                  onFocus={() => {
                    const v = range.max;
                    setPosPagoMaxRaw(index, (v !== undefined && v !== '' && Number.isFinite(Number(v)) ? formatarPlano(String(Math.floor(Number(v)))) : ''));
                  }}
                  onChange={(e) => {
                    const digitos = e.target.value.replace(/\D/g, '');
                    const limited = digitos.length > 9 ? digitos.slice(0, 9) : digitos;
                    setPosPagoMaxRaw(index, limited ? formatarPlano(limited) : '');
                    const valorNumerico = limited === '' ? 0 : parseInt(limited, 10);
                    if (valorNumerico <= 999999999) {
                      updateRangePosPago(index, 'max', valorNumerico);
                    }
                  }}
                  onBlur={() => {
                    if (index in rangePosPagoMaxRaw) {
                      const digitos = rangePosPagoMaxRaw[index].replace(/\D/g, '');
                      const valorNumerico = digitos === '' ? 0 : Math.min(999999999, parseInt(digitos, 10) || 0);
                      updateRangePosPago(index, 'max', valorNumerico);
                      clearPosPagoMaxRaw(index);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (["e", "E", "-", "+", ","].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                  disabled={isReadOnly || index === rangesPosPago.length - 1}
                  sx={{
                    minHeight: '80px',
                    '& .MuiFormHelperText-root': {
                      marginTop: '4px'
                    },
                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Valor Unitário (R$)"
                  type="text"
                  inputProps={{
                    min: 0,
                    step: 0.0001,
                    inputMode: "decimal",
                    pattern: "[0-9]*[.,]?[0-9]*"
                  }}
                  value={index in valorUnitarioPending.pos ? valorUnitarioPending.pos[index] : formatValorUnitario(range.valor)}
                  onChange={(e) => {
                    const digitos = e.target.value.replace(/\D/g, '').slice(0, 15);
                    if (!digitos) {
                      setPosPending(index, '');
                      updateRangePosPago(index, 'valor', 0);
                      return;
                    }

                    const valorNumerico = Number(digitos) / 10000;
                    const valorFormatado = formatValorUnitario(valorNumerico);
                    setPosPending(index, valorFormatado);
                    updateRangePosPago(index, 'valor', valorNumerico);
                  }}
                  onBlur={() => {
                    if (index in valorUnitarioPending.pos) {
                      clearPosPending(index);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (['e', 'E', '-', '+'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                  disabled={isReadOnly}
                  error={camposComErro.has(`rangePosPago${index}`) || camposComErro.has(`rangePosPagoDecimais${index}`)}
                  helperText={
                    camposComErro.has(`rangePosPagoDecimais${index}`)
                      ? 'Máximo 4 casas decimais'
                      : camposComErro.has(`rangePosPago${index}`)
                        ? 'Campo obrigatório'
                        : ''
                  }
                  sx={{
                    minHeight: '80px',
                    '& .MuiFormHelperText-root': {
                      marginTop: '4px'
                    },
                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0
                    },
                    '& input[type=number]': {
                      MozAppearance: 'textfield'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                {rangesPosPago.length > 1 && !isReadOnly && (
                  <IconButton
                    onClick={() => removeRangePosPago(index)}
                    color="error"
                    title="Remover range"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          </Box>
        ))}

        {!isReadOnly && (
          <Button
            startIcon={<AddIcon />}
            onClick={addRangePosPago}
            variant="outlined"
            size="small"
          >
            Adicionar Range
          </Button>
        )}
      </Grid>
    </Grid>
  );

  const renderPrePagoFields = () => (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <TextField
          fullWidth
          label="Quantidade"
          type="text"
          inputProps={{ min: 1 }}
          value={quantidadePreRaw.isRaw ? quantidadePreRaw.value : (quantidadePre ? formatarPlano(quantidadePre) : '')}
          onFocus={() => {
            setQuantidadePreRaw({ isRaw: true, value: quantidadePre ? formatarPlano(String(Math.floor(quantidadePre))) : '' });
          }}
          onChange={(e) => {
            const digitos = e.target.value.replace(/\D/g, '');
            const limited = digitos.length > 9 ? digitos.slice(0, 9) : digitos;
            setQuantidadePreRaw({ isRaw: true, value: limited ? formatarPlano(limited) : '' });
            const valorNumerico = limited === '' ? 0 : parseInt(limited, 10);
            dispatch(setQuantidadePre(valorNumerico));
          }}
          onBlur={() => {
            if (quantidadePreRaw.isRaw) {
              const digitos = quantidadePreRaw.value.replace(/\D/g, '');
              const valorNumerico = digitos === '' ? 0 : Math.min(999999999, parseInt(digitos, 10) || 0);
              dispatch(setQuantidadePre(valorNumerico));
              setQuantidadePreRaw({ isRaw: false, value: '' });
            }
          }}
          disabled={isReadOnly}
          error={camposComErro.has('quantidadePre')}
          helperText={camposComErro.has('quantidadePre') ? 'Campo obrigatório' : ''}
          onKeyDown={(e) => {
            if (["e", "E", "-", "+", ","].includes(e.key)) {
              e.preventDefault();
            }
          }}
          onWheel={(e) => (e.target as HTMLInputElement).blur()}
          sx={{
            '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
            '& input[type=number]': {
              MozAppearance: 'textfield',
            },
          }}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          label="Valor Total (R$)"
          type="text"
          inputProps={{ min: 0, step: 0.01, inputMode: 'decimal' }}
          value={formatValorMensalComRaw(valorTotalPreRaw, valorPre)}
          onFocus={() => {
            const valorFormatado = formatValorMensalComRaw({ isRaw: false, value: '' }, valorPre);
            setValorTotalPreRaw({ isRaw: true, value: valorFormatado === '0,00' ? '' : valorFormatado });
          }}
          onChange={(e) => {
            const digitos = e.target.value.replace(/\D/g, '').slice(0, 15);
            if (!digitos) {
              setValorTotalPreRaw({ isRaw: true, value: '' });
              dispatch(setValorPre(0));
              return;
            }

            const valorNumerico = Number(digitos) / 100;
            const valorFormatado = formatValorMensalComRaw({ isRaw: false, value: '' }, valorNumerico);
            setValorTotalPreRaw({ isRaw: true, value: valorFormatado });
            dispatch(setValorPre(valorNumerico));
          }}
          onBlur={() => {
            if (valorTotalPreRaw.isRaw) {
              setValorTotalPreRaw({ isRaw: false, value: '' });
            }
          }}
          disabled={isReadOnly}
          error={camposComErro.has('valorPre')}
          helperText={camposComErro.has('valorPre') ? 'Campo obrigatório' : ''}
          onKeyDown={(e) => {
            if (['e', 'E', '-', '+'].includes(e.key)) {
              e.preventDefault();
            }
          }}
          onWheel={(e) => (e.target as HTMLInputElement).blur()}
          sx={{
            '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
            '& input[type=number]': {
              MozAppearance: 'textfield',
            },
          }}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          label="Valor Unitário (R$) - Calculado Automaticamente"
          type="text"
          inputProps={{
            readOnly: true,
            step: 0.0001,
            inputMode: 'decimal'
          }}
          value={formatValorUnitarioPre(valorUnitarioPre)}
          disabled
          onWheel={(e) => (e.target as HTMLInputElement).blur()}
          sx={{
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
            },
            '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
            '& input[type=number]': {
              MozAppearance: 'textfield',
            },
          }}
        />
      </Grid>

      <Grid item xs={6}>
        <DatePicker
          label="Data de Expiração"
          value={expiracaoPre ?? undefined}
          onChange={(newValue) => dispatch(setExpiracaoPre(newValue))}
          disabled={isReadOnly}
          slotProps={{
            textField: {
              fullWidth: true,
              error: camposComErro.has('expiracaoPre'),
              helperText: camposComErro.has('expiracaoPre') ? 'Campo obrigatório' : '',
            },
          }}
          format="dd/MM/yyyy"
          minDate={new Date()}
        />
      </Grid>
    </Grid>
  );

  const renderPOCFields = () => (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <TextField
          fullWidth
          label="Quantidade Máxima"
          type="text"
          inputProps={{ min: 0 }}
          value={formatQuantidadeComRaw(quantidadePOCRaw, quantidadePOC)}
          onFocus={() => {
            setQuantidadePOCRaw({ isRaw: true, value: quantidadePOC ? formatarPlano(String(Math.floor(quantidadePOC))) : '' });
          }}
          onChange={(e) => {
            const digitos = e.target.value.replace(/\D/g, '');
            const limited = digitos.length > 9 ? digitos.slice(0, 9) : digitos;
            setQuantidadePOCRaw({ isRaw: true, value: limited ? formatarPlano(limited) : '' });
            const valorNumerico = limited === '' ? 0 : parseInt(limited, 10);
            if (valorNumerico <= 999999999) {
              dispatch(setQuantidadePOC(valorNumerico));
            }
          }}
          onBlur={() => {
            if (quantidadePOCRaw.isRaw) {
              const digitos = quantidadePOCRaw.value.replace(/\D/g, '');
              const valorNumerico = digitos === '' ? 0 : Math.min(999999999, parseInt(digitos, 10) || 0);
              dispatch(setQuantidadePOC(valorNumerico));
              setQuantidadePOCRaw({ isRaw: false, value: '' });
            }
          }}
          onWheel={(e) => (e.target as HTMLInputElement).blur()}
          disabled={isReadOnly}
          error={camposComErro.has('quantidadePOC')}
          helperText={camposComErro.has('quantidadePOC') ? 'Campo obrigatório' : ''}
          onKeyDown={(e) => {
            if (["e", "E", "-", "+", ","].includes(e.key)) {
              e.preventDefault();
            }
          }}
          sx={{
            '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
            '& input[type=number]': {
              MozAppearance: 'textfield',
            },
          }}
        />
      </Grid>

      <Grid item xs={6}>
        <DatePicker
          label="Data de Expiração"
          value={expiracaoPOC ?? undefined}
          onChange={(newValue) => dispatch(setExpiracaoPOC(newValue))}
          disabled={isReadOnly}
          slotProps={{
            textField: {
              fullWidth: true,
              error: camposComErro.has('expiracaoPOC'),
              helperText: camposComErro.has('expiracaoPOC') ? 'Campo obrigatório' : '',
            },
          }}
          format="dd/MM/yyyy"
          minDate={new Date()}
        />
      </Grid>
    </Grid>
  );

  const renderPlanoAtual = () => {
    if (!plano) return <></>;

    let planoReal: IPlanoCompletoMultiRange | false = false;

    if ((plano as any).data && (plano as any).data.plano && (plano as any).data.plano.length > 0) {
      planoReal = (plano as any).data.plano[0];
    } else if (plano.TipoPlano) {
      planoReal = plano;
    }

    if (!planoReal) return <></>;

    return (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Plano Atual
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary" component="div">
                Tipo: <Chip
                  label={tipoPlanoLabel[planoReal.TipoPlano]}
                  color="primary"
                  size="small"
                />
              </Typography>
            </Grid>
            {effectiveMode === 'edit' && (
              <>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary" component="div">
                    Status: <Chip
                      label={planoReal.PlanoClienteAtivo ? 'Ativo' : 'Inativo'}
                      color={planoReal.PlanoClienteAtivo ? 'success' : 'error'}
                      size="small"
                    />
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    Cliente: {planoReal.ClienteNome}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="primary">
                    CNPJ: {planoReal.ClienteDocumento}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <Box>
        {renderPlanoAtual()}

        <Typography variant="h6" gutterBottom>
          {effectiveMode === 'view' ? 'Visualizar Plano' : effectiveMode === 'edit' ? 'Editar Plano' : 'Criar Novo Plano'}
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Tipo de Plano</InputLabel>
              <Select
                value={tipoPlano}
                label="Tipo de Plano"
                onChange={handleTipoPlanoChange}
                disabled={isReadOnly}
              >
                <MenuItem value={TipoPlano.FeeMensal}>Fee Mensal</MenuItem>
                <MenuItem value={TipoPlano.PosPago}>Pós Pago</MenuItem>
                <MenuItem value={TipoPlano.PrePago}>Pré Pago</MenuItem>
                <MenuItem value={TipoPlano.POC}>POC</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {tipoPlano && (
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Configurações do Plano {tipoPlanoLabel[tipoPlano as TipoPlano]}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  {tipoPlano === TipoPlano.FeeMensal && renderFeeMensalFields()}
                  {tipoPlano === TipoPlano.PosPago && renderPosPagoFields()}
                  {tipoPlano === TipoPlano.PrePago && renderPrePagoFields()}
                  {tipoPlano === TipoPlano.POC && renderPOCFields()}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>
    </LocalizationProvider>
  );
});